import React, { FC, useEffect, useContext } from 'react';
import _ from 'lodash';
import { Skeleton } from 'antd';
import { formatNumWithKMBAlt, getConfig, removeFunctionFromProps } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
const G2 = require('@antv/g2');
import { Line } from '@antv/g2plot';
import useTrackTabChange from 'hooks/general/useTrackTabChange';

const defaultLabelWordLimit = 5;
//ADD ALL THE REQUIRED PROPS AT TOP
interface Props {
    fields: Array<string>;
    dataset: Array<any>;
    chartContainer: string;
    height: number;
    loading: boolean;
    colorKey: string;

    showLoader?: boolean;
    yAxisConfig?: boolean | object;
    xAxisConfig?: boolean | object;
    chartConfig?: object;
    yAxisLabelFormatter?: (val: any) => any;
    xAxisLabelFormatter?: (val: any) => any;
    tooltipConfig?: object;
    YGrid?: boolean;
    XGrid?: boolean;
    padding?: [number, number, number, number];
    width?: number;
    barWidth?: number;
    yAxisTitle?: string;
    xAxisTitle?: string;
    error?: any;
    additionalStyle?: any;
    minBound?: number;
    maxBound?: number;
    showLegend?: boolean;
    size?: number;
    showErrorBox?: boolean;
    Yline?: boolean;
    Xline?: boolean;
    tooltipFormatter?: (xAxis: any, val: any) => any;
    colorMapping?: any;
    lineStyle?: any;
    range?: [number, number];
    dashedLineType?: string;
}

const MultiLineChart: FC<Props> = ({
    yAxisConfig = true,
    xAxisConfig = false,
    chartConfig = {},
    height = 400,
    width,
    fields,
    xAxisTitle = '',
    yAxisTitle = '',
    colorKey = 'color',
    additionalStyle,
    dataset,
    chartContainer,
    tooltipConfig = {},
    loading,
    padding = [0, 0, 0, 0],
    error,
    barWidth = 20,
    YGrid = true,
    XGrid = false,
    Yline = false,
    Xline = true,
    showLoader = true,
    showErrorBox = true,
    minBound,
    showLegend = false,
    yAxisLabelFormatter,
    xAxisLabelFormatter,
    tooltipFormatter = (xAxis, val) => {
        return {
            name: xAxis,
            value: formatNumWithKMBAlt(val, 2, 2),
        };
    },
    maxBound,
    colorMapping = {},
    size = 4,
    lineStyle = {
        lineWidth: 0.9,
        opacity: 0.5,
    },
    range = [0, 1],
    dashedLineType = '(YA)',
}) => {
    // if (!loading && error && dataset?.length === 0 && showErrorBox) {
    //     return <ErrorBox msg={String(error)} />;
    // }

    //this is used to track tab change and
    //re render the canvas element when tabs are changed.
    const isWindowTabActive = useTrackTabChange();
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontSizeAlt, graphLabelColorAlt, graphFontFamily } = commonSetting;

    useEffect(() => {
        const chartId = document.getElementById(chartContainer) as HTMLElement | string;
        const linePlot: any = new Line(chartId, {
            width: width,
            height: height,
            autoFit: false,
            appendPadding: padding,
            color(datum, defaultColor) {
                const color = colorMapping[datum['type']];
                return color;
            },
            lineShape: (val) => {
                return val?.type === dashedLineType ? 'dash' : '';
            },
            tooltip: {
                showNil: true,
                showCrosshairs: false,
                showMarkers: false,
                formatter(data: any) {
                    return tooltipFormatter(data?.type, data?.[fields[1]]);
                },
                customItems: (items) => {
                    return items?.filter((item: any) => !_.isNil(item?.data?.[fields[1]])) || undefined;
                },
                // showContent: (data) => {
                //     const items = data?.filter((item: any) => !_.isNil(item?.data?.[fields[1]]));
                //     console.log(items, 'logging data');
                //     return items?.length > 0;
                // },
            },
            data: dataset,
            xField: fields[0],
            yField: fields[1],
            xAxis: !xAxisConfig
                ? false
                : {
                      label: {
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                  },
            yAxis: {
                range: range,
                max: maxBound,
                label: {
                    // 数值格式化为千分位

                    formatter: (v) => '',
                },
                min: minBound,
            },
            legend: false,
            lineStyle: lineStyle,
            point: {
                shape(datum) {
                    return 'square';
                },
                size: size,
            },
            seriesField: 'type',
        });
        linePlot.render();
        return () => {
            linePlot.destroy();
        };
    }, [dataset, fields?.[1], isWindowTabActive]);

    return (
        <div className="">
            {showLoader && (
                <>
                    <Skeleton loading={loading} />
                    <Skeleton loading={loading} />
                </>
            )}
            <div style={{ opacity: loading ? 0.3 : 1, ...additionalStyle }} id={chartContainer} />
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    const [clonePrev, cloneNext] = removeFunctionFromProps({ ...prevProps }, { ...nextProps });
    return _.isEqual(clonePrev, cloneNext);
}

export default React.memo(MultiLineChart, arePropsEqual);
